import { MapDataService } from '@ui/shared/domain-map';
import { map, type Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { SportAtlasService, SportsClubOrderingEnum } from '@ui/shared/util-api';
import { toRequestQuery } from '@ui/shared/util-filter';
import { SportMapItem } from '../../definitions/sport-map-item';
import type { SportMapFilter } from '../../types/sport-map-filter';
import { LinkedMapItem } from '../../definitions/linked-map-item';
import { TypeFilterOptions } from '../../definitions/filter-option-keys';

@Injectable({ providedIn: 'root' })
export class SportsClubMapDataService extends MapDataService<SportMapItem, SportMapFilter> {
    protected _api = inject(SportAtlasService);

    public getData$(filter: SportMapFilter): Observable<Array<SportMapItem>> {
        const typeId = toRequestQuery(filter.types)[0];

        if ((typeId && ![TypeFilterOptions.SportClub, TypeFilterOptions.All].includes(typeId))) {
            return of([]);
        }

        return this._api.getAllSportsClubs({
            nameContains: typeof filter.search === 'string' ? filter.search : null,
            sportsIds: toRequestQuery(filter.sports),
            ageGroupIds: toRequestQuery(filter.targetGroup),
            accessibilityFeatureIds: toRequestQuery(filter.accessibilityFeatures),
            order: [SportsClubOrderingEnum.Name],
        })
            .pipe(map(response => response.data.map(dto => new SportMapItem({
                sportsClub: dto,
                linkedItems: this._groupSportsById(
                    dto.facilities_for_sports
                        .map(facility => new LinkedMapItem({
                            name: facility.facility.name,
                            id: facility.facility.id,
                            type: TypeFilterOptions.SportFacility,
                            sports: facility.sports.map(sport => sport.name),
                        })),
                ),
            }))));
    }

    private _groupSportsById(list: Array<LinkedMapItem>): Array<LinkedMapItem> {
        return list.reduce(
            (newList, listEntry) => {
                const existing = newList.find(entry => entry.id === listEntry.id);

                if (existing) {
                    existing.sports.push(...listEntry.sports);
                } else {
                    newList.push(listEntry);
                }

                return newList;
            },
            new Array<LinkedMapItem>(),
        );
    }
}
